import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchCustomer } from '../actions/customers';
import { fetchEmailTemplateByCustomer, createEmailTemplate, updateEmailTemplate } from '../actions/emailTemplates';
import generateRentalCredentialsEmail from "../utils/emailTemplateGenerator";
import ErrorMessage from "../components/ErrorMessage";
import LoadingProgress from "../components/LoadingProgress";
import FeatureBlockedScreen from "../components/FeatureBlockedScreen";
import "../styles/EmailTemplateView.css";

class EmailTemplateView extends React.Component {
  constructor(props) {
    super(props);
    
    this.state = {
      loading: true,
      saving: false,
      template: null,
      error: null,
      successMessage: null,
      hasUnsavedChanges: false, // Track unsaved changes
      currentLanguage: 'es', // Current language being edited
      previewLanguage: 'es', // Language shown in preview
      selectedCustomer: null, // For admin mode
      activeTab: 'header', // Active form tab: header, colors, content, legal, social
      
      // Form data
      formData: {
        is_active: false,
        email_from_name: 'Vacway Tech',
        header_image_url: 'https://vacway-images.s3.eu-central-1.amazonaws.com/header_email.png',
        header_image_base64: '',
        header_link: 'www.vacway.com',
        header_bg_color: '#022040',
        primary_color: '#022040',
        secondary_color: '#000000',
        background_color: '#eee6e6',
        text_color: '#3e3e3e',
        font_family: 'Roboto, Arial',
        
        // Multi-language content
        es: {
          greeting_text: '¡Hola {name}!',
          intro_text: 'A continuación te proporcionamos los detalles de tu reserva y los códigos para acceder a las taquillas:',
          footer_info_text: 'Recuerda que dispones de tu taquilla hasta las 23:59 del día de fin de tu reserva. Retira tus pertenencias antes del horario de cierre de la tienda correspondiente.',
          footer_contact_text: 'Para cualquier duda, ponte en contacto con nosotros a través de support@vacway.com',
          legal_text: `Está recibiendo este mail porqué está registrado en la base de datos del grupo VACWAY TECH. En cumplimiento de lo dispuesto en el GDPR y LOPDGDD, le informamos de que los datos personales serán tratados por un interés legítimo y para el envío de comunicaciones sobre nuestros productos y servicios, y se conservarán mientras ninguna de las partes se oponga a ello, no serán cedidos a terceros, excepto al GRUPO EMPRESARIAL DE VACWAY TECH (Vacway Tech, S.L) o por obligación legal. Puede ejercer los derechos de acceso, rectificación, portabilidad, supresión, limitación y oposición enviando un mensaje a gpdr@vacway.com.

PUBLICIDAD: Si usted no desea recibir más información sobre nuestros productos y/o servicios, puede Cancelar suscripción

VACWAY TECH SL Travessera de gracia 17 08021 Barcelona (Barcelona) CIF B66983032.`
        },
        
        en: {
          greeting_text: 'Hello {name}!',
          intro_text: 'Below are the details of your reservation and the access codes for the lockers:',
          footer_info_text: 'Remember that you have your locker until 11:59 PM on the end date of your reservation. Please retrieve your belongings before the store closing time.',
          footer_contact_text: 'For any questions, please contact us at support@vacway.com',
          legal_text: `You are receiving this email because you are registered in the VACWAY TECH database. In compliance with GDPR regulations, we inform you that personal data will be processed for legitimate interest and for sending communications about our products and services, and will be retained as long as neither party objects, will not be transferred to third parties, except to the VACWAY TECH BUSINESS GROUP (Vacway Tech, S.L) or by legal obligation. You can exercise your rights of access, rectification, portability, deletion, limitation and opposition by sending a message to gpdr@vacway.com.

ADVERTISING: If you do not wish to receive more information about our products and/or services, you can Unsubscribe

VACWAY TECH SL Travessera de gracia 17 08021 Barcelona (Barcelona) TAX ID B66983032.`
        },
        
        company_info: 'VACWAY TECH SL Travessera de gracia 17\n08021 Barcelona (Barcelona) CIF B66983032',
        privacy_policy_url: 'https://vacway.com/locker-terms.html',
        social_instagram: 'https://www.instagram.com/vacway/',
        social_facebook: 'https://www.facebook.com/vacwaytech/',
        social_tiktok: 'https://www.tiktok.com/@vacway',
        social_linkedin: 'https://es.linkedin.com/company/vacway',
        social_twitter: 'https://twitter.com/vacway',
        social_bg_color: '#000000'
      },
      
      // Preview data
      previewData: {
        name: 'Juan Pérez',
        lockerMatricula: '30',
        lockerPin: '2017',
        lockerCustomer: 'Vacway Tech',
        lockerInvoice: 'QRVL000002000408',
        date: '12/9/2025 · 14:25',
        zoneSelected: '4',
        descriptionZone: 'Oficinas',
        doorAccess: '',
        isMode3: false
      }
    };
    
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleImageUpload = this.handleImageUpload.bind(this);
    this.removeHeaderImage = this.removeHeaderImage.bind(this);
  }

  handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match('image.*')) {
      alert('Please select a valid image file.');
      e.target.value = '';
      return;
    }

    // Validate file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is too large. Please select an image smaller than 5MB.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions (max 800px width for email headers)
        let width = img.width;
        let height = img.height;
        const maxWidth = 800;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Create canvas for compression
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try PNG first (for transparency), then JPEG if too large
        let compressedDataUrl = canvas.toDataURL('image/png');

        // If PNG is too large, try JPEG with white background
        if (compressedDataUrl.length > 40000) {
          let quality = 0.8;

          // Fill white background for JPEG
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, width, height);

          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

          // Reduce quality if still too large
          while (compressedDataUrl.length > 40000 && quality > 0.3) {
            quality -= 0.1;
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          }
        }

        if (compressedDataUrl.length > 50000) {
          alert('Image is too large even after compression. Please use a smaller or simpler image.');
          e.target.value = '';
          return;
        }

        this.setState(prevState => ({
          formData: {
            ...prevState.formData,
            header_image_base64: compressedDataUrl,
            header_image_url: '' // Clear URL when uploading custom image
          },
          hasUnsavedChanges: true
        }));
      };
      img.onerror = () => {
        alert('Error loading image. Please try another image.');
        e.target.value = '';
      };
      img.src = readerEvent.target.result;
    };
    reader.readAsDataURL(file);
  }

  removeHeaderImage() {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        header_image_base64: '',
        header_image_url: 'https://vacway-images.s3.eu-central-1.amazonaws.com/header_email.png'
      },
      hasUnsavedChanges: true
    }));
  }

  componentDidMount() {
    const { role, auth } = this.props;
    const urlCustomerId = this.props.match?.params?.customerId;
    
    // Determine which customer ID to use
    let customerId;
    if (role === 'ADMIN' && urlCustomerId) {
      // Admin mode - fetch customer details and their template
      customerId = urlCustomerId;
      this.props.fetchCustomer(customerId);
      this.props.fetchEmailTemplateByCustomer(customerId);
    } else {
      // Customer mode - fetch their own data and template
      customerId = auth?.id || this.props.customer?.id;
      if (customerId) {
        this.props.fetchCustomer(customerId);
        this.props.fetchEmailTemplateByCustomer(customerId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Update form data when template loads
    if (!prevProps.emailTemplate && this.props.emailTemplate) {
      this.setState({
        template: this.props.emailTemplate,
        formData: {
          ...this.state.formData,
          ...this.props.emailTemplate
        }
      });
    }
    
    // Store selected customer from Redux when loaded
    if (!prevProps.customer && this.props.customer && this.props.role === 'ADMIN') {
      this.setState({ selectedCustomer: this.props.customer });
    }
    
    // Clear 404 errors - they just mean no template exists yet (valid for new template creation)
    if (this.props.error && this.props.error.status === 404) {
      // 404 is expected when creating a new template
    }
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    const { currentLanguage } = this.state;
    
    // Language-specific fields
    const languageFields = ['greeting_text', 'intro_text', 'footer_info_text', 'footer_contact_text', 'legal_text'];
    
    if (languageFields.includes(name)) {
      // Update nested language field
      this.setState(prevState => ({
        formData: {
          ...prevState.formData,
          [currentLanguage]: {
            ...prevState.formData[currentLanguage],
            [name]: value
          }
        },
        hasUnsavedChanges: true
      }));
    } else {
      // Update regular field
      this.setState(prevState => ({
        formData: {
          ...prevState.formData,
          [name]: value
        },
        hasUnsavedChanges: true
      }));
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    try {
      this.setState({ saving: true, error: null, successMessage: null });
      
      const { role, auth } = this.props;
      const urlCustomerId = this.props.match?.params?.customerId;
      
      // Use URL customer ID if admin, otherwise use logged in customer
      const customerId = (role === 'ADMIN' && urlCustomerId) 
        ? urlCustomerId
        : (auth?.id || this.props.customer?.id);
      
      if (!customerId) {
        throw new Error('No se pudo obtener el ID del cliente');
      }

      const dataToSend = {
        ...this.state.formData,
        customer_id: customerId
      };

      const templateId = this.state.template?._id || this.state.template?.id
        || this.props.emailTemplate?._id || this.props.emailTemplate?.id;
      
      if (templateId) {
        // Update existing template
        await this.props.updateEmailTemplate(templateId, dataToSend);
      } else {
        // Create new template
        await this.props.createEmailTemplate(dataToSend);
        // Sync newly created template into local state immediately so
        // subsequent saves use the update path instead of trying to create again
        if (this.props.emailTemplate && !this.state.template) {
          this.setState({ template: this.props.emailTemplate });
        }
      }

      this.setState({ 
        saving: false, 
        successMessage: 'Plantilla guardada correctamente',
        hasUnsavedChanges: false
      });
      
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 3000);
      
    } catch (error) {
      console.error('Error saving template:', error);
      this.setState({ 
        saving: false, 
        error: error.message || 'Error al guardar la plantilla'
      });
    }
  }

  renderPreview() {
    try {
      const htmlContent = generateRentalCredentialsEmail(
        this.state.formData,
        this.state.previewData,
        this.state.previewLanguage
      );
      
      return (
        <iframe
          title="Email Preview"
          srcDoc={htmlContent}
          className="email-preview-iframe"
          sandbox="allow-same-origin"
        />
      );
    } catch (error) {
      return (
        <div className="preview-error">
          <p>Error al generar la vista previa</p>
          <p className="error-details">{error.message}</p>
        </div>
      );
    }
  }

  render() {
    const { saving, error, successMessage, formData, template, selectedCustomer } = this.state;
    const { role, loading, error: propsError, customer } = this.props;
    const isAdminMode = role === 'ADMIN' && selectedCustomer;

    if (role === 'CUSTOMER' && customer && customer.FeatureEmailTemplates === false) {
      return <FeatureBlockedScreen featureName="FeatureEmailTemplates" featureLabel="Plantillas de Email" />;
    }
    
    // Ignore 404 errors - they just mean no template exists yet
    const displayError = propsError && propsError.status !== 404 ? propsError : error;

    if (loading) {
      return <LoadingProgress />;
    }

    return (
      <div className="email-template-view">
        <div className="email-template-header">
          <div className="header-title-section">
            <div>
              <h1>
                {isAdminMode && (
                  <span className="customer-badge">
                    <i className="fas fa-user mr-2"></i>
                    {selectedCustomer?.Fullname}
                  </span>
                )}
                {isAdminMode ? ' - Plantilla de Email' : 'Plantilla de Email'}
              </h1>
              <p className="subtitle">
                {template 
                  ? 'Personaliza la plantilla de email para reservas de taquillas'
                  : 'Crea la primera plantilla de email para reservas de taquillas'
                }
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#666', margin: 0 }}>Remitente:</label>
                <input
                  type="text"
                  name="email_from_name"
                  value={formData.email_from_name}
                  onChange={this.handleInputChange}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '180px'
                  }}
                  placeholder="Vacway Tech"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#666', margin: 0 }}>Estado:</label>
                <div
                  onClick={() => {
                    this.setState(prevState => ({
                      formData: {
                        ...prevState.formData,
                        is_active: !prevState.formData.is_active
                      },
                      hasUnsavedChanges: true
                    }));
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: formData.is_active ? '#28a745' : '#dc3545',
                    borderRadius: '25px',
                    padding: '4px',
                    cursor: 'pointer',
                    width: '90px',
                    height: '32px',
                    position: 'relative',
                    transition: 'background-color 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                  title={formData.is_active ? 'Click para desactivar' : 'Click para activar'}
                >
                  <div
                    style={{
                      position: 'absolute',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      transition: 'transform 0.3s ease',
                      transform: formData.is_active ? 'translateX(60px)' : 'translateX(2px)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }}
                  />
                  <span
                    style={{
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      position: 'absolute',
                      left: formData.is_active ? '8px' : 'auto',
                      right: formData.is_active ? 'auto' : '8px',
                      userSelect: 'none',
                    }}
                  >
                    {formData.is_active ? 'ACTIVA' : 'INACTIVA'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {this.state.hasUnsavedChanges && !saving && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#856404',
                    fontWeight: '500'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{ color: '#ffc107' }}></i>
                    Cambios sin guardar
                  </div>
                )}
                <button
                  type="button"
                  onClick={this.handleSubmit}
                  className="btn btn-primary btn-save-header"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-small"></span> Guardando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Guardar Plantilla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {displayError && <ErrorMessage message={displayError.message || displayError} />}
        {successMessage && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i> {successMessage}
          </div>
        )}

        <div className="email-template-container">
          {/* Editor Panel */}
          <div className="editor-panel">
            <form onSubmit={this.handleSubmit}>
              {/* Tab Navigation */}
              <div className="form-tabs">
                <button
                  type="button"
                  className={`form-tab ${this.state.activeTab === 'header' ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: 'header' })}
                >
                  <i className="fas fa-heading"></i> Encabezado
                </button>
                <button
                  type="button"
                  className={`form-tab ${this.state.activeTab === 'colors' ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: 'colors' })}
                >
                  <i className="fas fa-palette"></i> Colores
                </button>
                <button
                  type="button"
                  className={`form-tab ${this.state.activeTab === 'content' ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: 'content' })}
                >
                  <i className="fas fa-align-left"></i> Contenido
                </button>
                <button
                  type="button"
                  className={`form-tab ${this.state.activeTab === 'legal' ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: 'legal' })}
                >
                  <i className="fas fa-gavel"></i> Legal
                </button>
                <button
                  type="button"
                  className={`form-tab ${this.state.activeTab === 'social' ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: 'social' })}
                >
                  <i className="fas fa-share-alt"></i> Redes
                </button>
              </div>

              <div className="form-sections">
                {/* Header Section */}
                {this.state.activeTab === 'header' && (
                <div className="form-section">
                  <h2 className="section-title">
                    <i className="fas fa-heading"></i> Encabezado
                  </h2>
                  
                  <div className="form-group">
                    <label>Imagen de Cabecera</label>
                    {formData.header_image_base64 ? (
                      <div className="image-preview-container">
                        <img 
                          src={formData.header_image_base64} 
                          alt="Header preview" 
                          className="header-image-preview"
                          style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px' }}
                        />
                        <button
                          type="button"
                          onClick={this.removeHeaderImage}
                          className="btn btn-danger btn-sm"
                        >
                          <i className="fas fa-trash"></i> Eliminar Imagen
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="header_image_upload"
                          accept="image/*"
                          onChange={this.handleImageUpload}
                          className="form-control"
                          style={{ marginBottom: '10px' }}
                        />
                        <small className="form-help">O usa una URL:</small>
                        <input
                          type="url"
                          id="header_image_url"
                          name="header_image_url"
                          value={formData.header_image_url}
                          onChange={this.handleInputChange}
                          className="form-control"
                          placeholder="https://..."
                        />
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="header_link">Enlace del Encabezado</label>
                    <input
                      type="url"
                      id="header_link"
                      name="header_link"
                      value={formData.header_link}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="header_bg_color">Color de Fondo del Encabezado</label>
                    <div className="color-input-group">
                      <input
                        type="color"
                        id="header_bg_color"
                        name="header_bg_color"
                        value={formData.header_bg_color}
                        onChange={this.handleInputChange}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={formData.header_bg_color}
                        onChange={this.handleInputChange}
                        name="header_bg_color"
                        className="color-text"
                        placeholder="#022040"
                      />
                    </div>
                  </div>
                </div>
                )}

                {/* Colors Section */}
                {this.state.activeTab === 'colors' && (
                <div className="form-section">
                  <h2 className="section-title">
                    <i className="fas fa-palette"></i> Colores
                  </h2>
                  
                  <div className="form-group">
                    <label htmlFor="primary_color">Color Primario</label>
                    <div className="color-input-group">
                      <input
                        type="color"
                        id="primary_color"
                        name="primary_color"
                        value={formData.primary_color}
                        onChange={this.handleInputChange}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={this.handleInputChange}
                        name="primary_color"
                        className="color-text"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="secondary_color">Color Secundario</label>
                    <div className="color-input-group">
                      <input
                        type="color"
                        id="secondary_color"
                        name="secondary_color"
                        value={formData.secondary_color}
                        onChange={this.handleInputChange}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color}
                        onChange={this.handleInputChange}
                        name="secondary_color"
                        className="color-text"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="background_color">Color de Fondo</label>
                    <div className="color-input-group">
                      <input
                        type="color"
                        id="background_color"
                        name="background_color"
                        value={formData.background_color}
                        onChange={this.handleInputChange}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={formData.background_color}
                        onChange={this.handleInputChange}
                        name="background_color"
                        className="color-text"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="text_color">Color de Texto</label>
                    <div className="color-input-group">
                      <input
                        type="color"
                        id="text_color"
                        name="text_color"
                        value={formData.text_color}
                        onChange={this.handleInputChange}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={formData.text_color}
                        onChange={this.handleInputChange}
                        name="text_color"
                        className="color-text"
                      />
                    </div>
                  </div>
                </div>
                )}

                {/* Content Section */}
                {this.state.activeTab === 'content' && (
                <div className="form-section">
                  <h2 className="section-title">
                    <i className="fas fa-align-left"></i> Contenido
                  </h2>
                  
                  {/* Language Tabs */}
                  <div className="language-tabs">
                    <button
                      type="button"
                      className={`language-tab ${this.state.currentLanguage === 'es' ? 'active' : ''}`}
                      onClick={() => this.setState({ currentLanguage: 'es' })}
                    >
                      <i className="fas fa-flag"></i> Español
                    </button>
                    <button
                      type="button"
                      className={`language-tab ${this.state.currentLanguage === 'en' ? 'active' : ''}`}
                      onClick={() => this.setState({ currentLanguage: 'en' })}
                    >
                      <i className="fas fa-flag"></i> English
                    </button>
                  </div>
                  
                  {/* Spanish Content */}
                  {this.state.currentLanguage === 'es' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="greeting_text">Texto de Saludo (Español)</label>
                        <input
                          type="text"
                          id="greeting_text"
                          name="greeting_text"
                          value={formData.es?.greeting_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          placeholder="¡Hola {name}!"
                        />
                        <small className="form-help">Usa {'{name}'} para el nombre del cliente</small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="intro_text">Texto de Introducción (Español)</label>
                        <textarea
                          id="intro_text"
                          name="intro_text"
                          value={formData.es?.intro_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          rows="3"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="footer_info_text">Texto Informativo del Pie (Español)</label>
                        <textarea
                          id="footer_info_text"
                          name="footer_info_text"
                          value={formData.es?.footer_info_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          rows="3"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="footer_contact_text">Texto de Contacto (Español)</label>
                        <textarea
                          id="footer_contact_text"
                          name="footer_contact_text"
                          value={formData.es?.footer_contact_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          rows="2"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* English Content */}
                  {this.state.currentLanguage === 'en' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="greeting_text">Greeting Text (English)</label>
                        <input
                          type="text"
                          id="greeting_text"
                          name="greeting_text"
                          value={formData.en?.greeting_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          placeholder="Hello {name}!"
                        />
                        <small className="form-help">Use {'{name}'} for customer name</small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="intro_text">Introduction Text (English)</label>
                        <textarea
                          id="intro_text"
                          name="intro_text"
                          value={formData.en?.intro_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          rows="3"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="footer_info_text">Footer Information Text (English)</label>
                        <textarea
                          id="footer_info_text"
                          name="footer_info_text"
                          value={formData.en?.footer_info_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          rows="3"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="footer_contact_text">Contact Text (English)</label>
                        <textarea
                          id="footer_contact_text"
                          name="footer_contact_text"
                          value={formData.en?.footer_contact_text || ''}
                          onChange={this.handleInputChange}
                          className="form-control"
                          rows="2"
                        />
                      </div>
                    </>
                  )}
                </div>
                )}

                {/* Legal Section */}
                {this.state.activeTab === 'legal' && (
                <div className="form-section">
                  <h2 className="section-title">
                    <i className="fas fa-balance-scale"></i> Legal
                  </h2>
                  
                  {/* Language Tabs */}
                  <div className="language-tabs">
                    <button
                      type="button"
                      className={`language-tab ${this.state.currentLanguage === 'es' ? 'active' : ''}`}
                      onClick={() => this.setState({ currentLanguage: 'es' })}
                    >
                      <i className="fas fa-flag"></i> Español
                    </button>
                    <button
                      type="button"
                      className={`language-tab ${this.state.currentLanguage === 'en' ? 'active' : ''}`}
                      onClick={() => this.setState({ currentLanguage: 'en' })}
                    >
                      <i className="fas fa-flag"></i> English
                    </button>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company_info">Información de la Empresa / Company Information</label>
                    <textarea
                      id="company_info"
                      name="company_info"
                      value={formData.company_info}
                      onChange={this.handleInputChange}
                      className="form-control"
                      rows="2"
                    />
                  </div>

                  {/* Spanish Legal Text */}
                  {this.state.currentLanguage === 'es' && (
                    <div className="form-group">
                      <label htmlFor="legal_text">Texto Legal (Español)</label>
                      <textarea
                        id="legal_text"
                        name="legal_text"
                        value={formData.es?.legal_text || ''}
                        onChange={this.handleInputChange}
                        className="form-control"
                        rows="6"
                      />
                    </div>
                  )}
                  
                  {/* English Legal Text */}
                  {this.state.currentLanguage === 'en' && (
                    <div className="form-group">
                      <label htmlFor="legal_text">Legal Text (English)</label>
                      <textarea
                        id="legal_text"
                        name="legal_text"
                        value={formData.en?.legal_text || ''}
                        onChange={this.handleInputChange}
                        className="form-control"
                        rows="6"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="privacy_policy_url">URL Política de Privacidad / Privacy Policy URL</label>
                    <input
                      type="url"
                      id="privacy_policy_url"
                      name="privacy_policy_url"
                      value={formData.privacy_policy_url}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                )}

                {/* Social Media Section */}
                {this.state.activeTab === 'social' && (
                <div className="form-section">
                  <h2 className="section-title">
                    <i className="fas fa-share-alt"></i> Redes Sociales
                  </h2>
                  
                  <div className="form-group">
                    <label htmlFor="social_bg_color">Color de Fondo Redes Sociales</label>
                    <div className="color-input-group">
                      <input
                        type="color"
                        id="social_bg_color"
                        name="social_bg_color"
                        value={formData.social_bg_color}
                        onChange={this.handleInputChange}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={formData.social_bg_color}
                        onChange={this.handleInputChange}
                        name="social_bg_color"
                        className="color-text"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="social_instagram">Instagram</label>
                    <input
                      type="url"
                      id="social_instagram"
                      name="social_instagram"
                      value={formData.social_instagram}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://www.instagram.com/..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="social_facebook">Facebook</label>
                    <input
                      type="url"
                      id="social_facebook"
                      name="social_facebook"
                      value={formData.social_facebook}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://www.facebook.com/..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="social_tiktok">TikTok</label>
                    <input
                      type="url"
                      id="social_tiktok"
                      name="social_tiktok"
                      value={formData.social_tiktok}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://www.tiktok.com/..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="social_linkedin">LinkedIn</label>
                    <input
                      type="url"
                      id="social_linkedin"
                      name="social_linkedin"
                      value={formData.social_linkedin}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://www.linkedin.com/..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="social_twitter">Twitter</label>
                    <input
                      type="url"
                      id="social_twitter"
                      name="social_twitter"
                      value={formData.social_twitter}
                      onChange={this.handleInputChange}
                      className="form-control"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>
                )}
              </div>
            </form>
          </div>

          {/* Preview Panel */}
          <div className="preview-panel">
            <div className="preview-header">
              <h2>
                <i className="fas fa-eye"></i> Vista Previa
              </h2>
              <p className="preview-note">
                Esta es una vista previa de cómo se verá el email
              </p>
              
              {/* Language Selector for Preview */}
              <div className="preview-language-selector">
                <label>Idioma de vista previa:</label>
                <div className="language-buttons">
                  <button
                    type="button"
                    className={`btn-language ${this.state.previewLanguage === 'es' ? 'active' : ''}`}
                    onClick={() => this.setState({ previewLanguage: 'es' })}
                  >
                    🇪🇸 Español
                  </button>
                  <button
                    type="button"
                    className={`btn-language ${this.state.previewLanguage === 'en' ? 'active' : ''}`}
                    onClick={() => this.setState({ previewLanguage: 'en' })}
                  >
                    🇬🇧 English
                  </button>
                </div>
              </div>
            </div>
            <div className="preview-container">
              {this.renderPreview()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    auth: state.auth,
    role: state.auth.role,
    customer: state.customers.customer,
    emailTemplate: state.emailTemplates.emailTemplate,
    loading: state.customers.loading || state.emailTemplates.loading,
    error: state.customers.error || state.emailTemplates.error
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCustomer,
    fetchEmailTemplateByCustomer,
    createEmailTemplate,
    updateEmailTemplate
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailTemplateView);
